// src/pages/SuspendedUsers.js
// Fraud Management → Suspended Users management page.
// Shows users/{uid} where status == "Inactive".
// Actions: View · Reinstate · View Fraud Logs · Blacklist · Copy UID

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Row, Col, Table, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

import Sidebar from '../components/Sidebar';
import DashboardNavbar from '../components/DashboardNavbar';
import useSuspendedUsers, { SU_PAGE_SIZE } from '../hooks/useSuspendedUsers';
import SuspendedUserViewDrawer from '../components/fraud/SuspendedUserViewDrawer';
import ReinstateConfirmDialog from '../components/fraud/ReinstateConfirmDialog';
import BlacklistEntryDialog from '../components/fraud/BlacklistEntryDialog';

// ─── Shared atoms ─────────────────────────────────────────────────────────────

const Toast = ({ toast }) => {
  if (!toast) return null;
  const isError = toast.type === 'error';
  return (
    <div
      style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 2000,
        background: isError ? 'rgba(239,68,68,0.92)' : 'rgba(34,197,94,0.92)',
        backdropFilter: 'blur(8px)', color: '#fff',
        borderRadius: 10, padding: '12px 20px',
        fontSize: 13, fontWeight: 600,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', gap: 10,
        maxWidth: 380, animation: 'suToastIn 0.25s ease',
      }}
    >
      <span>{isError ? '✗' : '✓'}</span>
      <span>{toast.message}</span>
      <style>{`@keyframes suToastIn{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
};

// ─── Summary card ─────────────────────────────────────────────────────────────
const StatCard = ({ id, title, subtitle, value, icon, accent, accentBg, loading, suffix }) => (
  <div id={`su-card-${id}`} className="theme-card rounded-3 px-4 py-3 h-100" style={{ minHeight: 100, position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: accent, borderRadius: '4px 0 0 4px' }} />
    <div style={{ position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: '50%', background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
      {icon}
    </div>
    <small className="body-text-three d-block" style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>{title}</small>
    <small style={{ fontSize: 10, color: 'var(--text-secondary)', opacity: 0.7 }}>{subtitle}</small>
    {loading
      ? <div className="fraud-skeleton mt-2" style={{ width: '55%', height: 28, borderRadius: 6 }} />
      : <h5 className="text-h2 mt-2 mb-0" style={{ color: accent, fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>
          {value !== null && value !== undefined
            ? <>{value}{suffix && <small style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400 }}>{suffix}</small>}</>
            : <span style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 400 }}>No data</span>}
        </h5>
    }
  </div>
);

// ─── Score pill ───────────────────────────────────────────────────────────────
const ScorePill = ({ score }) => {
  if (score === null || score === undefined)
    return <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</span>;
  const color = score >= 70 ? '#EF4444' : score >= 40 ? '#F59E0B' : '#22C55E';
  return <span style={{ fontWeight: 800, color, fontSize: 15 }}>{score}</span>;
};

// ─── Copy cell ────────────────────────────────────────────────────────────────
const CopyCell = ({ value, mono = false }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const short = value.length > 18 ? `${value.substring(0, 9)}…${value.substring(value.length - 5)}` : value;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span title={value} style={{ fontSize: 11, fontFamily: mono ? 'monospace' : 'inherit', cursor: 'default' }}>{short}</span>
      <button
        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }); }}
        title={copied ? 'Copied!' : `Copy: ${value}`}
        style={{ background: 'transparent', border: 'none', padding: '0 2px', cursor: 'pointer', color: copied ? '#22C55E' : 'var(--text-secondary)', fontSize: 11, flexShrink: 0 }}
      >
        {copied ? '✓' : '⧉'}
      </button>
    </span>
  );
};

// ─── Row actions dropdown ─────────────────────────────────────────────────────
const ActionsMenu = ({ user, onView, onReinstate, onViewLogs, onBlacklist, onCopyUid, opLoading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const item = (label, onClick, color) => (
    <button
      onClick={(e) => { e.stopPropagation(); setOpen(false); onClick(); }}
      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: color || 'var(--text-primary)', transition: 'background 0.1s' }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)} disabled={opLoading}
        style={{ background: open ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 6, color: 'var(--text-primary)', width: 28, height: 28, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        ⋮
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 32, zIndex: 900, background: 'var(--bg-surface)', border: '1px solid var(--divider)', borderRadius: 8, minWidth: 155, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
          {item('👁 View',             onView)}
          {item('✓ Reinstate',         onReinstate,  '#22C55E')}
          {item('📋 View Fraud Logs',  onViewLogs,   '#8B5CF6')}
          {item('🚫 Blacklist User',   onBlacklist,  '#EF4444')}
          {item('⧉ Copy UID',          onCopyUid)}
        </div>
      )}
    </div>
  );
};

// ─── Skeleton rows ────────────────────────────────────────────────────────────
const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, i) => (
    <tr key={i}>
      {[140, 160, 100, 50, 60, 80, 100, 120, 40].map((w, j) => (
        <td key={j} className="transparent-header py-3">
          <div className="fraud-skeleton" style={{ width: w, height: 12, borderRadius: 4 }} />
        </td>
      ))}
    </tr>
  ));

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ hasSearch }) => (
  <tr>
    <td colSpan={9} className="transparent-header text-center py-5">
      <div style={{ color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>{hasSearch ? '🔍' : '✅'}</div>
        <p style={{ fontSize: 14, marginBottom: 4 }}>
          {hasSearch ? 'No users match your search' : 'No suspended users'}
        </p>
        <small style={{ fontSize: 12 }}>
          {hasSearch ? 'Try a different search term' : 'All users are currently active'}
        </small>
      </div>
    </td>
  </tr>
);

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, hasPrev, hasNext, onPrev, onNext, total, loading }) => (
  <div className="d-flex justify-content-between align-items-center mt-3 px-1" style={{ flexWrap: 'wrap', gap: 8 }}>
    <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
      Page {page} of {totalPages} · {total} {total === 1 ? 'user' : 'users'} · {SU_PAGE_SIZE} per page
    </small>
    <div className="d-flex gap-2">
      <button onClick={onPrev} disabled={!hasPrev || loading} id="su-prev-btn"
        style={{ background: !hasPrev ? 'rgba(255,255,255,0.04)' : 'var(--bg-surface)', border: '1px solid var(--divider)', borderRadius: 6, color: !hasPrev ? 'var(--text-secondary)' : 'var(--text-primary)', padding: '5px 14px', cursor: !hasPrev ? 'default' : 'pointer', fontSize: 12, fontWeight: 600 }}>
        ← Prev
      </button>
      <button onClick={onNext} disabled={!hasNext || loading} id="su-next-btn"
        style={{ background: !hasNext ? 'rgba(255,255,255,0.04)' : 'var(--accent-lime)', border: 'none', borderRadius: 6, color: !hasNext ? 'var(--text-secondary)' : '#050014', padding: '5px 14px', cursor: !hasNext ? 'default' : 'pointer', fontSize: 12, fontWeight: 700 }}>
        Next →
      </button>
    </div>
  </div>
);

// ─── Date formatter ───────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── Page ────────────────────────────────────────────────────────────────────
const COLUMNS = ['User', 'Email', 'Phone', 'Fraud Score', 'High Risk Pmts', 'Suspended At', 'Suspended By', 'Reason', 'Actions'];

const SuspendedUsers = () => {
  const navigate = useNavigate();
  const su = useSuspendedUsers();
  const [hovered, setHovered] = useState(null);

  // Local state for the blacklist dialog save operation
  const [blOpLoading, setBlOpLoading] = useState(false);
  const [blToast, setBlToast] = useState(null);

  const showBlToast = (type, message) => {
    setBlToast({ type, message });
    setTimeout(() => setBlToast(null), 3500);
  };

  // Build a pre-filled "editEntry" for the BlacklistEntryDialog
  // We pass a sentinel _id '__new__' so the dialog runs in edit-form mode (pre-filled)
  // but our onSave handler intercepts it and calls addDoc instead
  const blacklistPrefill = su.blacklistTarget
    ? {
        _id: '__new__',
        uid:   su.blacklistTarget.uid   || '',
        email: su.blacklistTarget.email || '',
        phone: su.blacklistTarget.phoneNumber || '',
        ip:    '',
        reason: `Suspended user: ${su.blacklistTarget.suspensionReason || ''}`.trim(),
        notes: '',
        active: true,
      }
    : null;

  const handleBlacklistSave = useCallback(async (_id, formData) => {
    setBlOpLoading(true);
    try {
      const currentUser = auth.currentUser;
      const payload = {
        uid:       formData.uid.trim()   || null,
        email:     formData.email.trim() || null,
        phone:     formData.phone.trim() || null,
        ip:        formData.ip.trim()    || null,
        reason:    formData.reason.trim(),
        notes:     formData.notes.trim() || null,
        active:    formData.active,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.email || currentUser?.uid || 'admin',
      };
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
      await addDoc(collection(db, 'blacklist'), payload);
      su.setBlacklistOpen(false);
      showBlToast('success', 'User added to blacklist');
    } catch (err) {
      showBlToast('error', `Failed to blacklist: ${err.message}`);
    } finally {
      setBlOpLoading(false);
    }
  }, [su]);

  // View Fraud Logs: navigate to /fraud/logs; UID goes in URL for context
  const handleViewLogs = useCallback((user) => {
    navigate(`/fraud/logs?uid=${encodeURIComponent(user.uid)}`);
  }, [navigate]);

  const cards = [
    { id: 'total',    title: 'Total Suspended',   subtitle: 'Currently inactive',    value: su.stats.total,         icon: '🔒', accent: '#EF4444', accentBg: 'rgba(239,68,68,0.10)' },
    { id: 'avg',      title: 'Avg Fraud Score',   subtitle: 'Across suspended users', value: su.stats.avgScore,      icon: '📊', accent: '#F59E0B', accentBg: 'rgba(245,158,11,0.10)', suffix: '/100' },
    { id: 'highrisk', title: 'High Risk Users',   subtitle: 'Score ≥ 70',            value: su.stats.highRisk,      icon: '⚠️', accent: '#EF4444', accentBg: 'rgba(239,68,68,0.10)' },
    { id: 'today',    title: 'Suspended Today',   subtitle: 'Added today',            value: su.stats.suspendedToday, icon: '📅', accent: '#D9FF1F', accentBg: 'rgba(217,255,31,0.08)' },
  ];

  return (
    <div className="d-flex entire-bg-color">
      <Sidebar />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <DashboardNavbar />

        {/* ── Summary Cards ── */}
        <section className="py-4 nav-body-bg" style={{ marginTop: '70px' }}>
          <Container fluid className="px-4">
            <Row className="g-3">
              {cards.map((c) => (
                <Col key={c.id} xs={12} sm={6} xl={3}>
                  <StatCard {...c} loading={su.loading} />
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ── Body ── */}
        <section className="py-4 nav-body-bg btm-rad">
          <Container fluid className="px-4">

            {/* Page heading + search bar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: 12 }}>
              <div>
                <h3 className="text-h2 mb-0">Suspended Users</h3>
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--divider)', borderRadius: 20, padding: '3px 12px' }}>
                {su.loading ? 'Loading…' : `${su.filtered.length} ${su.filtered.length === 1 ? 'user' : 'users'}`}
              </span>
            </div>

            {/* Search + Refresh */}
            <div id="su-filter-bar" className="theme-card rounded-3 p-3 mb-4" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <div style={{ flex: 1, minWidth: 240 }}>
                  <Form.Control
                    id="su-search-input"
                    type="text"
                    placeholder="Search UID, name, email, phone…"
                    value={su.search}
                    onChange={(e) => su.setSearch(e.target.value)}
                    style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: 13 }}
                  />
                </div>
                {su.search && (
                  <button onClick={() => su.setSearch('')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 12, padding: '6px 12px', cursor: 'pointer' }}>
                    Clear
                  </button>
                )}
                <button
                  id="su-refresh-btn"
                  onClick={su.fetchAll}
                  disabled={su.loading}
                  title="Refresh"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: 'var(--text-secondary)', padding: '6px 14px', cursor: su.loading ? 'default' : 'pointer', fontSize: 14 }}
                >
                  {su.loading ? '…' : '↻'}
                </button>
              </div>
            </div>

            {/* Error banner */}
            {su.error && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 12, color: '#EF4444' }}>
                ⚠️ {su.error}
              </div>
            )}

            {/* Table */}
            <div id="su-table" className="theme-card rounded-3 p-4">
              <div className="table-responsive">
                <Table className="align-middle body-text-two mb-0" style={{ minWidth: 900 }}>
                  <thead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <th key={col} className="transparent-header" style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', paddingBottom: 10 }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {su.loading ? (
                      <SkeletonRows />
                    ) : su.pageUsers.length === 0 ? (
                      <EmptyState hasSearch={su.search.trim() !== ''} />
                    ) : (
                      su.pageUsers.map((user, idx) => (
                        <tr
                          key={user.uid}
                          onClick={() => su.openView(user)}
                          onMouseEnter={() => setHovered(idx)}
                          onMouseLeave={() => setHovered(null)}
                          style={{ borderBottom: '1px solid var(--divider)', cursor: 'pointer', background: hovered === idx ? 'rgba(217,255,31,0.04)' : 'transparent', transition: 'background 0.15s', verticalAlign: 'middle' }}
                        >
                          {/* User */}
                          <td className="transparent-header py-3">
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 1 }}>{user.name || <span style={{ color: 'var(--text-secondary)' }}>(no name)</span>}</div>
                            <CopyCell value={user.uid} mono />
                          </td>

                          {/* Email */}
                          <td className="transparent-header py-3">
                            <CopyCell value={user.email} />
                          </td>

                          {/* Phone */}
                          <td className="transparent-header py-3" style={{ fontSize: 12 }}>
                            {user.phoneNumber || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                          </td>

                          {/* Fraud Score */}
                          <td className="transparent-header py-3">
                            <ScorePill score={user.fraudScore} />
                          </td>

                          {/* High Risk Payments */}
                          <td className="transparent-header py-3" style={{ fontSize: 13 }}>
                            {user.highRiskPayments > 0
                              ? <span style={{ color: '#F59E0B', fontWeight: 700 }}>{user.highRiskPayments}</span>
                              : <span style={{ color: 'var(--text-secondary)' }}>0</span>
                            }
                          </td>

                          {/* Suspended At */}
                          <td className="transparent-header py-3" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            {fmtDate(user.suspendedAt)}
                          </td>

                          {/* Suspended By */}
                          <td className="transparent-header py-3" style={{ fontSize: 11 }}>
                            {user.suspendedBy || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                          </td>

                          {/* Reason */}
                          <td className="transparent-header py-3" style={{ maxWidth: 160 }}>
                            {user.suspensionReason
                              ? <span title={user.suspensionReason} style={{ fontSize: 11, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{user.suspensionReason}</span>
                              : <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>—</span>
                            }
                          </td>

                          {/* Actions */}
                          <td className="transparent-header py-3">
                            <ActionsMenu
                              user={user}
                              opLoading={su.opLoading}
                              onView={() => su.openView(user)}
                              onReinstate={() => su.openReinstate(user)}
                              onViewLogs={() => handleViewLogs(user)}
                              onBlacklist={() => su.openBlacklist(user)}
                              onCopyUid={() => navigator.clipboard.writeText(user.uid)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <Pagination
                page={su.page} totalPages={su.totalPages}
                hasPrev={su.hasPrev} hasNext={su.hasNext}
                onPrev={() => su.setPage((p) => p - 1)}
                onNext={() => su.setPage((p) => p + 1)}
                total={su.filtered.length} loading={su.loading}
              />
            </div>

          </Container>
        </section>

        <div style={{ height: 40 }} />
      </div>

      {/* ── View drawer ── */}
      <SuspendedUserViewDrawer
        user={su.viewUser}
        open={su.viewOpen}
        onClose={() => su.setViewOpen(false)}
        onReinstate={su.openReinstate}
        onViewLogs={handleViewLogs}
        onBlacklist={su.openBlacklist}
      />

      {/* ── Reinstate confirm ── */}
      <ReinstateConfirmDialog
        open={su.reinstateOpen}
        user={su.reinstateTarget}
        onClose={() => su.setReinstateOpen(false)}
        onConfirm={su.reinstateUser}
        opLoading={su.opLoading}
      />

      {/* ── Blacklist dialog (reused from Blacklist page, pre-filled) ── */}
      <BlacklistEntryDialog
        open={su.blacklistOpen}
        editEntry={blacklistPrefill}
        onClose={() => su.setBlacklistOpen(false)}
        onSave={handleBlacklistSave}
        opLoading={blOpLoading}
      />

      {/* ── Toasts ── */}
      <Toast toast={su.toast} />
      <Toast toast={blToast} />
    </div>
  );
};

export default SuspendedUsers;
